import playerConfig from './config'
import { addQueryParams, toQuery } from 'shared/javascripts/util/query'
import { getConsentString } from 'frontend/shared/javascripts/util/gdpr_consent'
import session from 'frontend/shared/javascripts/util/session'
import { appVersionAtLeast } from 'shared/javascripts/util/detection'
import { isIOS, isNativeApp } from 'shared/javascripts/util/detection'
import { COMPANY_IDENTIFIER_PARAMETER, LINE_ITEM_ID_PARAMETER } from './ad_company'

const { ad_server: adServerConfig } = playerConfig

interface TagConfig {
  base_url: string
  params: Record<string, string>
  track: { type: string }
  gdpr_vendors?: string[]
  filter?: Record<string, string[]>
  frequency_capping?: unknown
  iu_by_site?: Record<string, string>
}

class VASTClient {
  private tag: string

  constructor(tag: string) {
    this.tag = tag
  }

  async fetchAdResponse(): Promise<AdsResponse> {
    return resolveAdsResponse(await fetchXML(this.tag))
  }

  /**
   * Splits an ad pod into one standalone VAST per ad, so each can play in a player of its own. An ad whose
   * wrapper cannot be resolved is left out.
   */
  async fetchAdPodResponses(): Promise<AdsResponse[]> {
    const podDocument = parseXML(await fetchXML(this.tag))
    const ads = Array.from(podDocument.querySelectorAll('VAST > Ad'))
      .filter((ad) => ad.querySelector(':scope > InLine, :scope > Wrapper'))
      .sort((ad, otherAd) => podSequence(ad) - podSequence(otherAd))

    const results = await Promise.allSettled(
      ads.map((ad) => resolveAdsResponse(standaloneVAST(podDocument, ad))),
    )

    return results.flatMap((result) =>
      result.status === 'fulfilled' && result.value.firstAd ? [result.value] : [],
    )
  }
}

async function resolveAdsResponse(vastWrapperXML: string): Promise<AdsResponse> {
  const vastWrapperXMLDocument = parseXML(vastWrapperXML)
  let vastInlineXMLDocument: Document | null = null

  const vastAdTagURL = vastWrapperXMLDocument.querySelector('Wrapper > VASTAdTagURI')

  if (vastAdTagURL) {
    vastInlineXMLDocument = parseXML(await fetchXML(vastAdTagURL.textContent!))
  }

  return new AdsResponse(vastWrapperXML, vastWrapperXMLDocument, vastInlineXMLDocument)
}

// Ads without a sequence are standalone alternatives to the pod and go last.
function podSequence(ad: Element): number {
  const sequence = parseInt(ad.getAttribute('sequence') ?? '', 10)
  return Number.isNaN(sequence) ? Infinity : sequence
}

// Without its sequence the ad is no longer part of a pod, so IMA plays it as a single ad.
function standaloneVAST(podDocument: Document, ad: Element): string {
  const vast = podDocument.documentElement.cloneNode(false) as Element
  const standaloneAd = ad.cloneNode(true) as Element
  standaloneAd.removeAttribute('sequence')
  vast.appendChild(standaloneAd)

  return new XMLSerializer().serializeToString(vast)
}

function parseXML(xml: string): Document {
  return new DOMParser().parseFromString(xml, 'text/xml')
}

async function fetchXML(uri: string): Promise<string> {
  return (await fetch(uri)).text()
}

export class AdsResponse {
  vastWrapperXML: string
  vastWrapperXMLDocument: Document
  vastMainXMLDocument: Document

  constructor(
    vastWrapperXML: string,
    vastWrapperXMLDocument: Document,
    vastInlineXMLDocument: Document | null,
  ) {
    this.vastWrapperXML = vastWrapperXML
    this.vastWrapperXMLDocument = vastWrapperXMLDocument
    this.vastMainXMLDocument = vastInlineXMLDocument || vastWrapperXMLDocument
  }

  containsAd(): boolean {
    return this.vastWrapperXML.length > 1000
  }

  get firstAd(): Element | null {
    return this.vastMainXMLDocument.querySelector('Ad')
  }

  get adID(): string {
    return this.firstAd!.id
  }

  get duration(): number | undefined {
    const durationElement = this.vastMainXMLDocument.querySelector('Linear > Duration')

    if (durationElement !== null) {
      const offsetToNumber = durationElement.textContent!.match(/\d{2}:(\d{2}):(\d{2})/)!
      return parseInt(offsetToNumber[1]) * 60 + parseInt(offsetToNumber[2])
    }
  }

  get creativeID(): string | null {
    return this.vastMainXMLDocument.querySelector('Creative')?.id || null
  }

  /**
   * `<AdParameters>` hold JSON or a single `key=value`, the custom parameters as entered in Google Ad
   * Manager. The value is everything after the first `=`, verbatim: company identifiers carry `&` and
   * `+`, which nobody in ad ops should have to encode.
   */
  get adParameters(): Record<string, string> {
    const adParameters = this.vastMainXMLDocument.querySelector('Linear > AdParameters')
    const text = adParameters?.textContent?.trim()

    if (!text) {
      return {}
    }

    if (text.startsWith('{')) {
      try {
        const parsed: unknown = JSON.parse(text)
        return isStringRecord(parsed) ? parsed : {}
      } catch {
        return {}
      }
    }

    const separator = text.indexOf('=')
    return separator > 0
      ? { [text.slice(0, separator).trim()]: text.slice(separator + 1).trim() }
      : {}
  }

  /** The smallest video file of the ad this browser can play, for a still of it shown outside IMA. */
  get previewVideoUrl(): string | null {
    const video = document.createElement('video')
    const mediaFiles = Array.from(
      this.vastMainXMLDocument.querySelectorAll(
        'Linear > MediaFiles > MediaFile[delivery=progressive]',
      ),
    ).filter((mediaFile) => video.canPlayType(mediaFile.getAttribute('type') ?? '') !== '')
    const width = (mediaFile: Element): number =>
      parseInt(mediaFile.getAttribute('width') ?? '', 10) || Infinity
    const [smallest] = mediaFiles.sort((a, b) => width(a) - width(b))

    return smallest?.textContent?.trim() || null
  }

  get companyIdentifier(): string | null {
    return this.adParameters[COMPANY_IDENTIFIER_PARAMETER] || null
  }

  /** Only as JSON `<AdParameters>`: the `key=value` form carries a single parameter. */
  get lineItemId(): string | null {
    return this.adParameters[LINE_ITEM_ID_PARAMETER] || null
  }

  get clickThroughUrl(): string | null {
    const clickThroughElement = this.vastMainXMLDocument.querySelector('ClickThrough')
    let clickThroughUrl = null

    if (clickThroughElement) {
      clickThroughUrl = clickThroughElement.textContent!.trim()
    }

    return clickThroughUrl
  }

  get clickTrackingUrl(): string | null {
    const clickTrackingElement = this.vastWrapperXMLDocument.querySelector('ClickTracking')
    let clickTrackingUrl = null

    if (clickTrackingElement) {
      clickTrackingUrl = clickTrackingElement.textContent!.trim()
    }

    return clickTrackingUrl
  }

  get completeTrackingUrl(): string | null {
    const completeTrackingElement = this.vastWrapperXMLDocument.querySelector(
      'Tracking[event=complete]',
    )
    let completeTrackingUrl = null

    if (completeTrackingElement) {
      completeTrackingUrl = completeTrackingElement.textContent!.trim()
    }

    return completeTrackingUrl
  }

  /** From wrapper and inline both: each ad server in the chain counts its own skips. */
  get skipTrackingUrls(): string[] {
    const documents = new Set([this.vastWrapperXMLDocument, this.vastMainXMLDocument])

    return Array.from(documents)
      .flatMap((document) => Array.from(document.querySelectorAll('Tracking[event=skip]')))
      .map((tracking) => tracking.textContent!.trim())
      .filter(Boolean)
  }
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((entry) => typeof entry === 'string')
  )
}

function interpolateQueryParam(
  value: string,
  { iu_by_site: iuBySite }: { iu_by_site?: Record<string, string> },
  { keyValues, site }: { keyValues: Record<string, string>; site: string },
): string | number {
  switch (value) {
    case '[iuForSite]':
      return iuBySite![site || 'default']
    case '[random]':
      return Math.floor(Math.random() * 1000000000000) + 1
    case '[currentUrl]':
      return location.href
    case '[keyValues]':
      return toQuery(keyValues)
    case '[timestamp]':
      return new Date().getTime()
    default:
      return value
  }
}

async function buildTag(
  { base_url: baseUrl, params, track, gdpr_vendors: gdprVendors, ...tagConfig }: TagConfig,
  data: { keyValues: Record<string, string>; site: string },
): Promise<{ tag: string; track: { type: string } }> {
  const interpolatedQueryParams: Record<string, string | number> = {}
  for (const [key, value] of Object.entries(params)) {
    interpolatedQueryParams[key] = interpolateQueryParam(value, tagConfig, data)
  }

  if (gdprVendors && !appVersionAtLeast(1, 0, 4)) {
    interpolatedQueryParams.gdpr = 1
    const consentString = await getConsentString()
    if (consentString !== undefined) {
      interpolatedQueryParams.gdpr_consent = consentString
    }
  }

  return {
    tag: addQueryParams(baseUrl, interpolatedQueryParams),
    track,
  }
}

function tagAllowedByFilter(
  filter: Record<string, string[]> | undefined,
  keyValues: Record<string, string>,
): boolean {
  filter = filter || {}
  for (const [filterKey, filterValues] of Object.entries(filter)) {
    if (Object.prototype.hasOwnProperty.call(keyValues, filterKey)) {
      const value = keyValues[filterKey]
      let values: string[]
      if (value.split) {
        values = value.split(',')
      } else {
        values = [value]
      }
      if (values.every((value) => filterValues.indexOf(value) === -1)) {
        return false
      }
    }
  }
  return true
}

function frequencyCapped(tagConfig: TagConfig): boolean {
  if (tagConfig.frequency_capping) {
    const date = session.frequencyCappingDate
    return new Date().toDateString() === date
  } else {
    return false
  }
}

async function buildTagsWithMetadata(
  keyValues: Record<string, string>,
  site: string,
  adUnit: string,
): Promise<Array<{ tag: string; track: { type: string }; isFrequencyCapped: boolean }>> {
  const tagsWithMetadata: Array<{
    tag: string
    track: { type: string }
    isFrequencyCapped: boolean
  }> = []
  const configs = (adServerConfig[adUnit] ?? []) as TagConfig[]
  for (const tagConfig of configs) {
    if (tagAllowedByFilter(tagConfig.filter, keyValues) && !frequencyCapped(tagConfig)) {
      const { tag, track } = await buildTag(tagConfig, { keyValues, site })
      tagsWithMetadata.push({
        tag,
        track,
        isFrequencyCapped: tagConfig.frequency_capping !== undefined,
      })
    }
  }
  return tagsWithMetadata
}

export async function getAdsResponse(
  keyValues: Record<string, string>,
  site: string,
  adUnit: string,
): Promise<[AdsResponse | null, boolean]> {
  const tagsWithMetadata = await buildTagsWithMetadata(keyValues, site, adUnit)
  let primary = true
  for (const { tag, track, isFrequencyCapped } of tagsWithMetadata) {
    const adsResponse = await new VASTClient(tag).fetchAdResponse()

    if (adsResponse.containsAd()) {
      window.lastTrack = track
      window.lastTrackisFrequencyCapped = isFrequencyCapped

      if (adsResponse.firstAd) {
        session.setLastAdId(adUnit, adsResponse.adID)
      }

      return [adsResponse, primary]
    } else {
      primary = false
    }
  }
  return [null, false]
}

/** For ad units that request a pod (`pmad`): every ad of the first tag that fills, as standalone VAST. */
export async function getAdPodResponses(
  keyValues: Record<string, string>,
  site: string,
  adUnit: string,
): Promise<AdsResponse[]> {
  const tagsWithMetadata = await buildTagsWithMetadata(keyValues, site, adUnit)
  for (const { tag, track, isFrequencyCapped } of tagsWithMetadata) {
    const adsResponses = await new VASTClient(tag).fetchAdPodResponses()

    if (adsResponses.length > 0) {
      window.lastTrack = track
      window.lastTrackisFrequencyCapped = isFrequencyCapped
      session.setLastAdId(adUnit, adsResponses[0].adID)

      return adsResponses
    }
  }
  return []
}

export interface AdClickTracking {
  adClickElement:
    | 'main_cta_primary'
    | 'main_open_website'
    | 'main_cta_secondary'
    | 'main_company_ad_slider'
  elementText: string
}

export function clickAd(player: VjsPlayer, tracking?: AdClickTracking): void {
  const clickThroughUrl = player.ima.controller.settings.adsInfo.clickThroughUrl
  if (!clickThroughUrl) {
    return
  }

  const clickTrackingUrl = player.ima.controller.settings.adsInfo.clickTrackingUrl
  if (clickTrackingUrl) {
    // Partially copied from the request sent in https://googleads.github.io/googleads-ima-html5/vsi/ with a nested VAST
    // NOTE: Left out a few headers like "Accept" with images types, not sure if this is necessary
    void fetch(clickTrackingUrl, { mode: 'no-cors' })
  }

  if (isNativeApp()) {
    window.StudyflixApp!.postMessage(JSON.stringify({ command: 'openLink', url: clickThroughUrl }))
  } else {
    window.open(clickThroughUrl, '_blank')
  }

  if (tracking) {
    player.emitEvent('app:player:ad-clicked', tracking)
  } else {
    player.emitEvent('app:player:ad-clicked')
  }
}

/** Pauses the ad so it is still waiting when the viewer comes back from the advertiser's site. */
export function pauseAndClickAd(player: VjsPlayer, tracking?: AdClickTracking): void {
  player.ima.pauseAd()
  // iOS keeps the ad playing in the background when the advertiser's site opens in the same task.
  if (isIOS()) {
    setTimeout(() => clickAd(player, tracking), 0)
  } else {
    clickAd(player, tracking)
  }
}

/**
 * Ends the ad break early, also for ads Google Ad Manager doesn't mark as skippable, which IMA's
 * `skip()` refuses. `stop()` hands back to the content through videojs-ima, but IMA neither pings the
 * VAST skip trackers nor fires COMPLETE or SKIPPED for it, so both happen here. It does fire
 * ALL_ADS_COMPLETED, so `app:player:adBreakFinished` follows as usual.
 */
export function closeAd(player: VjsPlayer): void {
  const adsManager = player.ima.getAdsManager()
  if (!adsManager) {
    return
  }

  const { duration, skipTrackingUrls } = player.ima.controller.settings.adsInfo
  const watchedSeconds = Math.max((duration ?? 0) - adsManager.getRemainingTime(), 0)
  for (const skipTrackingUrl of skipTrackingUrls) {
    void fetch(withAdMediaTime(skipTrackingUrl, watchedSeconds), { mode: 'no-cors' })
  }

  player.emitEvent('app:player:adFinished')
  adsManager.stop()
}

export function reportAdComplete(player: VjsPlayer): void {
  const completeTrackingUrl = player.ima.controller.settings.adsInfo.completeTrackingUrl
  if (completeTrackingUrl) {
    void fetch(withAdMediaTime(completeTrackingUrl, player.duration()!), { mode: 'no-cors' })
  }
}

function withAdMediaTime(trackingUrl: string, seconds: number): string {
  return trackingUrl.replace('%5BAD_MT%5D', String(Math.round(seconds * 1000)))
}
